import React from 'react';
import { Calendar, Heart, Baby, ShieldAlert, Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета овуляции
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Форматирование дат для вывода
  const formatDateForDisplay = (date) => {
    if (!date) return 'Не определено';
    
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    };
    
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('ru-RU', options);
    
    // Добавляем день недели с большой буквы
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  };

  // Расчет фертильного окна (строковое представление)
  const fertileWindowString = results.fertileStartDate && results.fertileEndDate
    ? `${formatDate(results.fertileStartDate, 'medium')} - ${formatDate(results.fertileEndDate, 'medium')}`
    : 'Не определено';

  // Расчет наиболее фертильных дней (строковое представление)
  const peakFertileWindowString = results.peakFertileStartDate && results.peakFertileEndDate
    ? `${formatDate(results.peakFertileStartDate, 'medium')} - ${formatDate(results.peakFertileEndDate, 'medium')}`
    : 'Не определено';

  return (
    <ToolResult title="Результаты расчета овуляции">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* День овуляции */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <Heart className="w-4 h-4"/>
            День овуляции
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatDate(results.ovulationDate, 'medium')}
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            {results.ovulationDateAdjusted 
              ? 'Скорректировано с учетом дополнительных данных'
              : 'Рассчитано на основе длительности цикла'}
          </p>
        </div>

        {/* Следующая менструация */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <Calendar className="w-4 h-4"/>
            Следующая менструация
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {formatDate(results.nextPeriodDate, 'medium')}
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            Примерная дата начала следующего цикла
          </p>
        </div>
      </div>

      {/* Основной блок результатов */}
      <div className="space-y-3 mb-6">
        {formData.plannedPregnancy ? (
          <ResultItem 
            label="Наиболее благоприятные дни для зачатия" 
            value={peakFertileWindowString}
            color="rose" 
            icon={<Baby className="w-4 h-4"/>}
          />
        ) : (
          <ResultItem 
            label="Дни высокого риска зачатия" 
            value={peakFertileWindowString}
            color="rose" 
            icon={<AlertCircle className="w-4 h-4"/>}
          />
        )}
        
        {formData.plannedPregnancy ? (
          <ResultItem 
            label="Фертильное окно (благоприятные дни)" 
            value={fertileWindowString} 
            color="pink" 
            icon={<Heart className="w-4 h-4"/>}
          />
        ) : (
          <ResultItem 
            label="Дни, требующие защиты" 
            value={fertileWindowString} 
            color="orange" 
            icon={<ShieldAlert className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Текущая менструация" 
          value={`${formatDate(new Date(formData.lastPeriodDate), 'medium')} - ${formatDate(results.periodEndDate, 'medium')}`} 
          color="purple" 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Длительность цикла" 
          value={`${results.cycleLength} дней`} 
          icon={<CalendarIcon className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Лютеиновая фаза" 
          value={`${results.lutealPhaseLength} дней`} 
          icon={<CalendarIcon className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Длительность менструации" 
          value={`${results.periodLength} дней`} 
          color="purple" 
        />
      </div>

      {/* Информация о точности расчета */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важная информация о точности</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Расчет основан на календарном методе и является приблизительным. Точность повышается при регулярных циклах.
              {formData.includeSymptoms && formData.ovulationTestDate && " Учтены данные теста на овуляцию."} 
              {formData.includeSymptoms && formData.includeBbt && " Учтены данные о базальной температуре."}
            </p>
            {!formData.plannedPregnancy && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 font-medium">
                Для контрацепции используйте дополнительные методы защиты, так как календарный метод не является надежным средством предотвращения беременности.
              </p>
            )}
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;